import { Component, Input, forwardRef, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CheckboxOption, CheckboxTreeNode, CheckboxTreeValue } from '../primitives.types';

/**
 * kt-checkbox-tree — two-level collapsible checkbox tree.
 *
 * Each top-level node renders as a collapsible parent (chevron + checkbox) with
 * its children below. Two parent semantics via `parentMode`:
 *  - 'select-all'  : parent is a master toggle for its children (checked/indeterminate roll-up).
 *  - 'independent' : parent and children are mutually-exclusive selections
 *                    (checking parent clears children; checking a child unchecks parent).
 *
 * CVA value is a `CheckboxTreeValue` ({ parents, children }). In 'select-all' mode
 * `parents` stays empty — read the per-parent `children` arrays (or flatten them).
 */
@Component({
  selector: 'kt-checkbox-tree',
  templateUrl: './checkbox-tree.component.html',
  styleUrls: ['./checkbox-tree.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CheckboxTreeComponent), multi: true }]
})
export class CheckboxTreeComponent implements ControlValueAccessor, OnChanges {
  @Input() nodes: CheckboxTreeNode[] = [];
  @Input() parentMode: 'select-all' | 'independent' = 'select-all';
  @Input() collapsedDir: 'right' | 'down' = 'right';
  @Input() disabled = false;

  /** parentValue -> open/closed */
  expanded: { [key: string]: boolean } = {};
  /** Stable CheckboxOption[] per parent for the inner kt-checkbox-group */
  childOptions: { [key: string]: CheckboxOption[] } = {};

  value: CheckboxTreeValue = { parents: [], children: {} };

  onChange = (_: any) => { };
  onTouched = () => { };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['nodes']) {
      this.childOptions = {};
      for (const node of this.nodes) {
        if (node.expanded === undefined && this.expanded[node.value] === undefined) {
          this.expanded[node.value] = true;
        } else if (node.expanded !== undefined) {
          this.expanded[node.value] = node.expanded;
        }
        this.childOptions[node.value] = (node.children ?? []).map(c => ({
          value: c.value, label: c.label, disabled: c.disabled, tooltip: c.tooltip
        }));
      }
      this.ensureChildArrays();
    }
  }

  /** Guarantee a stable array reference per node so [ngModel] bindings don't churn each CD. */
  private ensureChildArrays() {
    for (const node of this.nodes) {
      if (!Array.isArray(this.value.children[node.value])) this.value.children[node.value] = [];
    }
  }

  // ── expand / chevron ──
  toggleExpand(node: CheckboxTreeNode) { this.expanded[node.value] = !this.expanded[node.value]; }
  isExpanded(node: CheckboxTreeNode): boolean { return !!this.expanded[node.value]; }
  chevronIcon(opened: boolean): string {
    if (this.collapsedDir === 'right') return opened ? 'keyboard_arrow_down' : 'chevron_right';
    return opened ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  }

  // ── selection state helpers ──
  private selectableChildValues(node: CheckboxTreeNode): any[] {
    return (node.children ?? []).filter(c => !c.disabled).map(c => c.value);
  }
  private selectedChildren(node: CheckboxTreeNode): any[] {
    return this.value.children[node.value] || [];
  }

  isParentChecked(node: CheckboxTreeNode): boolean {
    if (this.parentMode === 'independent') return this.value.parents.includes(node.value);
    const selectable = this.selectableChildValues(node);
    const selected = this.selectedChildren(node);
    return selectable.length > 0 && selectable.every(v => selected.includes(v));
  }

  isParentIndeterminate(node: CheckboxTreeNode): boolean {
    if (this.parentMode === 'independent') {
      return !this.value.parents.includes(node.value) && this.selectedChildren(node).length > 0;
    }
    const selectable = this.selectableChildValues(node);
    const selected = this.selectedChildren(node);
    return selectable.some(v => selected.includes(v)) && !this.isParentChecked(node);
  }

  childValues(node: CheckboxTreeNode): any[] { return this.selectedChildren(node); }

  // ── change handlers ──
  onParentChange(node: CheckboxTreeNode, checked: boolean) {
    const next = this.clone();
    if (this.parentMode === 'independent') {
      next.children[node.value] = [];
      next.parents = next.parents.filter(p => p !== node.value);
      if (checked) next.parents.push(node.value);
    } else {
      // select-all: keep already-selected disabled children, add/remove selectable ones
      const disabledSelected = this.selectedChildren(node).filter(
        v => (node.children ?? []).some(c => c.value === v && c.disabled)
      );
      next.children[node.value] = checked
        ? [...disabledSelected, ...this.selectableChildValues(node)]
        : disabledSelected;
    }
    this.commit(next);
  }

  onChildGroupChange(node: CheckboxTreeNode, childVals: any[]) {
    const next = this.clone();
    if (this.parentMode === 'independent') {
      next.parents = next.parents.filter(p => p !== node.value);
    }
    next.children[node.value] = childVals ?? [];
    this.commit(next);
  }

  private clone(): CheckboxTreeValue {
    return { parents: [...this.value.parents], children: { ...this.value.children } };
  }
  private commit(next: CheckboxTreeValue) {
    this.value = next;
    this.onChange(next);
    this.onTouched();
  }

  // ── CVA ──
  writeValue(value: CheckboxTreeValue) {
    this.value = value ?? { parents: [], children: {} };
    if (!this.value.parents) this.value.parents = [];
    if (!this.value.children) this.value.children = {};
    this.ensureChildArrays();
  }
  registerOnChange(fn: any) { this.onChange = fn; }
  registerOnTouched(fn: any) { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean) { this.disabled = isDisabled; }
}
